import * as React from 'react';
import { useIsMobile } from '../../../hooks/use-mobile';
import { cn } from '../../../utils/cn';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '../sheet';

const ResponsiveDialogContext = React.createContext(false);

function useResponsiveDialog(): boolean {
  return React.useContext(ResponsiveDialogContext);
}

/**
 * A centred Dialog on desktop and a bottom Sheet on mobile, from one set of
 * children.
 */
function ResponsiveDialog(props: React.ComponentProps<typeof Dialog>) {
  const isMobile = useIsMobile();
  const Root = isMobile ? Sheet : Dialog;
  useUnlockBodyOnClose(props.open ?? false);
  return (
    <ResponsiveDialogContext value={isMobile}>
      <Root {...props} />
    </ResponsiveDialogContext>
  );
}

/**
 * Radix's scroll-lock can leave `pointer-events: none` on `<body>` after a
 * mid-transition close, freezing the page.
 */
function useUnlockBodyOnClose(open: boolean): void {
  React.useEffect(() => {
    if (open) return;
    const timer = window.setTimeout(() => {
      if (document.body.style.pointerEvents !== 'none') return;
      const openLayer = document.querySelector(
        '[data-state="open"][role="dialog"], [data-state="open"][role="alertdialog"], [data-state="open"][role="menu"]',
      );
      if (!openLayer) document.body.style.pointerEvents = '';
    }, 0);
    return () => window.clearTimeout(timer);
  }, [open]);
}

/**
 * A body-portaled combobox list renders outside the dialog, so its clicks must
 * not dismiss it.
 */
function allowComboboxInteraction(event: {
  target: EventTarget | null;
  preventDefault: () => void;
}) {
  const target = event.target;
  if (target instanceof Element && target.closest('[data-combobox-list]')) event.preventDefault();
}

const ResponsiveDialogContent = React.forwardRef<
  React.ElementRef<typeof DialogContent>,
  React.ComponentPropsWithoutRef<typeof DialogContent>
>(({ className, overlayClassName, ...props }, ref) => {
  const isMobile = useResponsiveDialog();
  // the sheet draws its own backdrop, so the dialog's backdrop classes stay with the dialog
  if (isMobile)
    return (
      <SheetContent
        ref={ref}
        side="bottom"
        className={cn('max-h-[90vh] overflow-y-auto rounded-t-xl', className)}
        onInteractOutside={allowComboboxInteraction}
        onPointerDownOutside={allowComboboxInteraction}
        {...props}
      />
    );
  return (
    <DialogContent
      ref={ref}
      className={className}
      overlayClassName={overlayClassName}
      onInteractOutside={allowComboboxInteraction}
      onPointerDownOutside={allowComboboxInteraction}
      {...props}
    />
  );
});
ResponsiveDialogContent.displayName = 'ResponsiveDialogContent';

function ResponsiveDialogHeader(props: React.HTMLAttributes<HTMLDivElement>) {
  const isMobile = useResponsiveDialog();
  const Header = isMobile ? SheetHeader : DialogHeader;
  return <Header {...props} />;
}

function ResponsiveDialogFooter(props: React.HTMLAttributes<HTMLDivElement>) {
  const isMobile = useResponsiveDialog();
  const Footer = isMobile ? SheetFooter : DialogFooter;
  return <Footer {...props} />;
}

const ResponsiveDialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogTitle>,
  React.ComponentPropsWithoutRef<typeof DialogTitle>
>((props, ref) => {
  const isMobile = useResponsiveDialog();
  const Title = isMobile ? SheetTitle : DialogTitle;
  return (
    <Title
      ref={ref}
      {...props}
    />
  );
});
ResponsiveDialogTitle.displayName = 'ResponsiveDialogTitle';

const ResponsiveDialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogDescription>,
  React.ComponentPropsWithoutRef<typeof DialogDescription>
>((props, ref) => {
  const isMobile = useResponsiveDialog();
  const Description = isMobile ? SheetDescription : DialogDescription;
  return (
    <Description
      ref={ref}
      {...props}
    />
  );
});
ResponsiveDialogDescription.displayName = 'ResponsiveDialogDescription';

export {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
};
